/* @flow */
/**
 * Manages GAC change history report search criteria, results, bookmarks, exports, and pagination.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import {
  initialGACChangeHistoryReportFieldState,
  gacChangeHistoryReportFieldList
} from '../../constants/staticData';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import appConfig from '../../appConfig';
import {
  mapInputFieldsToRequest, mapRequestToInputFields, checkForColumnData, setValidFields
} from '../../helpers/dataHelpers';
import gacChangeHistoryReportHeader from '../../constants/gacChangeHistoryReportHeader';
import gacChangeHistoryReportFieldMapping from '../../constants/fieldMappings/gacChangeHistoryReportFieldMapping';
import { toggleSpinner, togglePaginationSpinner, updateDialogStates } from '../dialogStates/dialogStatesSlice';
import getFieldsList from './gacChangeHelper';
import gacChangeHistoryReportFieldProperties from '../../constants/fieldProperties/gacChangeHistoryReportFieldProperties';
import { showForTypePayload } from '../../helpers/envServer';

const name = 'gacChangeHistoryReport';

function createInitialState() {
  return {
    isSavedSearchModified: false,
    gacChangeHistoryReportRequestBody: null,
    gacChangeHistoryReportInputFieldData: { ...initialGACChangeHistoryReportFieldState },
    gacChangeHistoryReportSelectedFields: [],
    gacChangeHistoryReportBookmarkName: '',
    gacChangeHistoryReportBookmarkId: '',
    requestError: false,
    gacChangeHistorySubtableRequestBody: null,
    gacChangeHistoryReportResultData: {},
    gacChangeHistoryReportSearchId: '',
    gacChangeHistoryReportTotalCount: 0,
    gacChangeHistoryReportNextResults: {},
    gacChangeHistoryReportPrevResults: {},
    page: 0,
    rowsPerPage: 100,
    gacChangeHistorySubtableResults: {},
    jumpToPage: 0,
    apiCallAfterAppend: false,
    apiCallBeforeAppend: false
  };
}
export const initialState = createInitialState();
// request Reducer
/**
 * Reducer that stores the active report request body and flags saved searches when modified.
 */
function updateGACChangeHistoryReportRequestBody(state: Object, action: Object) {
  // Compare serialized requests so saved-search changes are detected across nested criteria.
  const requestExist = JSON.stringify(state.gacChangeHistoryReportRequestBody);
  return {
    ...state,
    gacChangeHistoryReportRequestBody: action.payload,
    isSavedSearchModified: Boolean(
      state.gacChangeHistoryReportBookmarkId && requestExist !== JSON.stringify(action.payload)
    )
  };
}
/**
 * Reducer that stores the request body used for GAC change history subtable data.
 */
function updateGACChangeHistorySubtableRequestBody(state: Object, action: Object) {
  return {
    ...state,
    gacChangeHistorySubtableRequestBody: action.payload
  };
}
/**
 * Reducer that explicitly sets whether the saved search has unsaved changes.
 */
function updateIsSavedSearchModified(state: Object, action: Object) {
  return {
    ...state,
    isSavedSearchModified: Boolean(action.payload)
  };
}
/**
 * Reducer that updates the request body during pagination without marking the bookmark modified.
 */
function updateGACChangeHistoryRequestBodyOnSameBookmark(state: Object, action: Object) {
  return {
    ...state,
    gacChangeHistoryReportRequestBody: action.payload
  };
}
/**
 * Reducer that stores selected GAC change history report field keys.
 */
function updateGACChangeHistoryReportFields(state: Object, action: Object) {
  return {
    ...state, gacChangeHistoryReportSelectedFields: action.payload
  };
}
/**
 * Reducer that stores search panel input values for the report.
 */
function updateGACChangeHistoryReportInputValues(state: Object, action: Object) {
  return {
    ...state, gacChangeHistoryReportInputFieldData: action.payload
  };
}
/**
 * Reducer that resets report input values to the default field state.
 */
function resetGACChangeHistoryReportInputData(state: Object) {
  return {
    ...state,
    gacChangeHistoryReportInputFieldData: initialState.gacChangeHistoryReportInputFieldData
  };
}
/**
 * Reducer that stores the active bookmark ID and clears the modified flag.
 */
function updateGACChangeHistoryReportBookmarkId(state: Object, action: Object) {
  return {
    ...state,
    isSavedSearchModified: false,
    gacChangeHistoryReportBookmarkId: action.payload
  };
}
/**
 * Reducer that stores the active bookmark name and clears the modified flag.
 */
function updateGACChangeHistoryReportBookmarkName(state: Object, action: Object) {
  return {
    ...state,
    isSavedSearchModified: false,
    gacChangeHistoryReportBookmarkName: action.payload
  };
}
/**
 * Reducer that clears the report request, selected fields, bookmark, and subtable state.
 */
function resetGACChangeHistoryReportRequest(state: Object) {
  return {
    ...state,
    isSavedSearchModified: false,
    gacChangeHistoryReportRequestBody: null,
    gacChangeHistoryReportInputFieldData: { ...initialGACChangeHistoryReportFieldState },
    gacChangeHistoryReportSelectedFields: [],
    gacChangeHistoryReportBookmarkName: '',
    gacChangeHistoryReportBookmarkId: '',
    requestError: false,
    gacChangeHistorySubtableRequestBody: null
  };
}
/**
 * Reducer that clears saved-search bookmark metadata from the report state.
 */
function clearSavedGACChangeHistoryReport(state: Object) {
  return {
    ...state,
    isSavedSearchModified: false,
    gacChangeHistoryReportBookmarkId: '',
    gacChangeHistoryReportBookmarkName: ''
  };
}
/**
 * Reducer that clears search criteria while preserving the selected field set.
 */
function resetGACChangeHistoryRequestForClearSearch(state: Object) {
  return {
    ...state,
    isSavedSearchModified: false,
    gacChangeHistoryReportRequestBody: null,
    gacChangeHistoryReportInputFieldData: { ...initialGACChangeHistoryReportFieldState },
    gacChangeHistoryReportSelectedFields: [
      ...new Set(state.gacChangeHistoryReportSelectedFields)
    ],
    gacChangeHistoryReportBookmarkName: '',
    gacChangeHistoryReportBookmarkId: '',
    requestError: false,
    gacChangeHistorySubtableRequestBody: null
  };
}
// result reducer
/**
 * Reducer that stores offset-based report results and resets cached adjacent result sets.
 */
function gacChangeHistoryReportOffset(state: Object, action: Object) {
  const payload = {
    // API responses include nested objects; flatten before storing rows in the report table.
    data: getflattenResponse(action?.payload?.data)
  };
  return {
    ...state,
    gacChangeHistoryReportResultData: payload.data,
    gacChangeHistoryReportTotalCount: payload?.data?.pages?.totalResources,
    gacChangeHistoryReportSearchId: cryptoRand().toString().substr(2, 8),
    selectedRecordsCount: 0,
    gacChangeHistoryReportNextResults: null,
    gacChangeHistoryReportPrevResults: null
  };
}
/**
 * Reducer that stores fresh report search results and resets pagination caches.
 */
function updateGACChangeHistoryReportResults(state: Object, action: Object) {
  const payload = {
    data: getflattenResponse(action.payload.data),
    dataNext: null
  };
  return {
    ...state,
    gacChangeHistoryReportResultData: payload.data,
    gacChangeHistoryReportTotalCount: payload?.data?.pages?.totalResources,
    gacChangeHistoryReportSearchId: cryptoRand().toString().substr(2, 8),
    selectedRecordsCount: 0,
    gacChangeHistoryReportNextResults: null,
    gacChangeHistoryReportPrevResults: null
  };
}
/**
 * Reducer that stores flattened subtable results for the selected report row.
 */
function updateGACChangeHistorySubtableResults(state: Object, action: Object) {
  const payload = {
    data: getflattenResponse(action.payload.data),
    dataNext: null
  };
  return {
    ...state,
    gacChangeHistorySubtableResults: payload
  };
}
/**
 * Reducer that refreshes the report search identifier used to trigger consumers.
 */
function updateGACChangeHistoryReportSearchId(state: Object) {
  return { ...state, gacChangeHistoryReportSearchId: cryptoRand().toString().substr(2, 8) };
}
/**
 * Reducer that stores the current report page index.
 */
function updateGACChangeHistoryReportPage(state: Object, action: Object) {
  return { ...state, page: action.payload };
}
/**
 * Reducer that stores the current report rows-per-page setting.
 */
function updateGACChangeHistoryReportRowsPerPage(state: Object, action: Object) {
  return { ...state, rowsPerPage: action.payload };
}
/**
 * Reducer that caches the next result set while retaining the current result set.
 */
function updateGACChangeHistoryReportNextResult(state: Object, action: Object) {
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataNext: getflattenResponse(action.payload.response.data)
  };
  return {
    ...state,
    gacChangeHistoryReportResultData: payload.data,
    gacChangeHistoryReportNextResults: payload.dataNext,
    gacChangeHistoryReportTotalPages: payload.data.pages.totalPages,
    gacChangeHistoryReportTotalCount: payload.data.pages.totalResources
  };
}

/**
 * Reducer that advances to the cached next result set and retains the previous set.
 */
function ChangeReportAppendNextResultSet(state: Object) {
  const updatedCurrentResultSet = state.gacChangeHistoryReportResultData;
  const updatedNextResultSet = state.gacChangeHistoryReportNextResults;
  const payload = {
    dataPrev: updatedCurrentResultSet,
    data: updatedNextResultSet
  };
  return {
    ...state,
    gacChangeHistoryReportResultData: payload.data,
    gacChangeHistoryReportPrevResults: payload.dataPrev,
    gacChangeHistoryReportNextResults: null
  };
}

/**
 * Reducer that advances after a jump-to-page request and recalculates page-window API flags.
 */
function ChangeReportAppendJumpToPageNextResultSet(state: Object, action: Object) {
  const updatedCurrentResultSet = state.gacChangeHistoryReportResultData;
  const updatedNextResultSet = state.gacChangeHistoryReportNextResults;
  const payload = {
    dataPrev: updatedCurrentResultSet,
    data: updatedNextResultSet
  };

  const numberString = state.page.toString();

  // Result windows are fetched in 1,000-row chunks, so boundary pages depend on row size.
  const rowPerPage = action.payload;

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString[numberString.length - 1] === '9';
    checkApiCallPrepend = numberString[numberString.length - 1] === '0';
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return {
    ...state,
    gacChangeHistoryReportResultData: payload.data,
    gacChangeHistoryReportPrevResults: payload.dataPrev,
    gacChangeHistoryReportNextResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Reducer that caches the previous result set while retaining the current result set.
 */
function updateGACChangeHistoryReportPrevResult(state: Object, action: Object) {
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataPrev: getflattenResponse(action.payload.response.data)
  };
  return {
    ...state,
    gacChangeHistoryReportResultData: payload.data,
    gacChangeHistoryReportPrevResults: payload.dataPrev,
    gacChangeHistoryReportTotalPages: payload?.data?.pages?.totalPages,
    gacChangeHistoryReportTotalCount: payload?.data?.pages?.totalResources
  };
}

/**
 * Reducer that moves back to the cached previous result set and retains the next set.
 */
function ChangeReportAppendPrevResultSet(state: Object) {
  const updatedPrevResultSet = state.gacChangeHistoryReportPrevResults;
  const updatedCurrentResultSet = state.gacChangeHistoryReportResultData;
  const payload = {
    data: updatedPrevResultSet,
    dataNext: updatedCurrentResultSet
  };

  return {
    ...state,
    gacChangeHistoryReportResultData: payload.data,
    gacChangeHistoryReportNextResults: payload.dataNext,
    gacChangeHistoryReportPrevResults: null
  };
}

/**
 * Reducer that moves back after a jump-to-page request and recalculates page-window API flags.
 */
function ChangeReportAppendJumpToPagePrevResultSet(state: Object, action: Object) {
  const updatedPrevResultSet = state.gacChangeHistoryReportPrevResults;
  const updatedCurrentResultSet = state.gacChangeHistoryReportResultData;
  const payload = {
    data: updatedPrevResultSet,
    dataNext: updatedCurrentResultSet
  };

  const numberString = state.page.toString();

  const rowPerPage = action.payload;

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString[numberString.length - 1] === '9';
    checkApiCallPrepend = numberString[numberString.length - 1] === '0';
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return {
    ...state,
    gacChangeHistoryReportResultData: payload.data,
    gacChangeHistoryReportNextResults: payload.dataNext,
    gacChangeHistoryReportPrevResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Reducer that clears report results and restores pagination defaults.
 */
function resetGACChangeHistoryReportResults(state: Object) {
  return {
    ...state,
    gacChangeHistoryReportResultData: {},
    gacChangeHistoryReportSearchId: '',
    gacChangeHistoryReportTotalCount: 0,
    gacChangeHistoryReportNextResults: {},
    gacChangeHistoryReportPrevResults: {},
    page: 0,
    rowsPerPage: 100,
    gacChangeHistorySubtableResults: {}
  };
}

/**
 * Reducer that clears the cached next result set.
 */
function updateNextResultSettoNull(state: Object) {
  return {
    ...state,
    gacChangeHistoryReportNextResults: null
  };
}

/**
 * Reducer that clears the cached previous result set.
 */
function updatePreviousResultSettoNull(state: Object) {
  return {
    ...state,
    gacChangeHistoryReportPrevResults: null
  };
}

/**
 * Reducer that stores a requested page jump and marks it for pagination handling.
 */
function updateJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    jumpToPage: action.payload,
    triggerJumpToPage: true
  };
}
/**
 * Reducer that toggles whether the next pagination call should handle a page jump.
 */
function updateTriggerJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    triggerJumpToPage: action.payload
  };
}

/**
 * Reducer that stores whether the next page window should be fetched after appending.
 */
function updateApiCallAfterAppend(state: Object, action: Object) {
  return {
    ...state,
    apiCallAfterAppend: action.payload
  };
}

/**
 * Reducer that stores whether the previous page window should be fetched before prepending.
 */
function updateApiCallBeforePrepend(state: Object, action: Object) {
  return {
    ...state,
    apiCallBeforeAppend: action.payload
  };
}

/**
 * Builds the reducer map consumed by the GAC change history report slice.
 */
function createReducers() {
  return {
    updateGACChangeHistoryReportRequestBody,
    updateIsSavedSearchModified,
    updateGACChangeHistorySubtableRequestBody,
    updateGACChangeHistoryRequestBodyOnSameBookmark,
    updateGACChangeHistoryReportFields,
    updateGACChangeHistoryReportInputValues,
    resetGACChangeHistoryReportInputData,
    updateGACChangeHistoryReportBookmarkId,
    updateGACChangeHistoryReportBookmarkName,
    resetGACChangeHistoryReportRequest,
    clearSavedGACChangeHistoryReport,
    resetGACChangeHistoryRequestForClearSearch,
    gacChangeHistoryReportOffset,
    updateGACChangeHistoryReportResults,
    updateGACChangeHistorySubtableResults,
    updateGACChangeHistoryReportSearchId,
    updateGACChangeHistoryReportPage,
    updateGACChangeHistoryReportRowsPerPage,
    updateGACChangeHistoryReportNextResult,
    ChangeReportAppendNextResultSet,
    updateGACChangeHistoryReportPrevResult,
    ChangeReportAppendPrevResultSet,
    resetGACChangeHistoryReportResults,
    ChangeReportAppendJumpToPageNextResultSet,
    ChangeReportAppendJumpToPagePrevResultSet,
    updateJumpToPage,
    updateTriggerJumpToPage,
    updateApiCallAfterAppend,
    updateApiCallBeforePrepend,
    updatePreviousResultSettoNull,
    updateNextResultSettoNull
  };
}

export const reducers = createReducers();
const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Async thunk that builds a report search request and calls gacChangeHistorySearch.
 */
function fetchGACChangeHistoryReports() {
  return createAsyncThunk(
    `${name}/fetchGACChangeHistoryReports`,
    async ({
      data,
      callback,
      modifiedrequestBody = {},
      inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const secondaryFields = [];
      // Default header metadata supplies required fields when no column selection exists.
      getFieldsList(secondaryFields);
      const state = getState();
      let requestData = {};
      const {
        selectedColumnOrderOptions: columnOrderOptions,
        rowPerPageOption: rowsPerPageOption
      } = state.searchpaneldata || {};
      // Include primary fields for configured request-only columns when columns are customized.
      if (checkForColumnData(state, true)) {
        gacChangeHistoryReportHeader.forEach((headRow: Object) => {
          if (headRow.includeInRequest) {
            columnOrderOptions.push(headRow.primary);
          }
        });
      }
      const fieldsForRequest = checkForColumnData(state, true)
        ? [...new Set(columnOrderOptions)]
        : secondaryFields;
      if (data) {
        requestData = mapInputFieldsToRequest(data, 'gacChangeHistoryReport');
        const appliedGACChangeHistorySearchField = new Set(
          requestData.map((field) => field.fieldName)
        );
        dispatch(actions.updateGACChangeHistoryReportInputValues(data));
        dispatch(actions.updateGACChangeHistoryReportFields(gacChangeHistoryReportFieldList
          .map((field) => field.key)
          .filter((field) => appliedGACChangeHistorySearchField.has(field))));
      } else {
        requestData = state.gacChangeHistoryReport.gacChangeHistoryReportRequestBody.search;
      }

      // Ensure searches only include rows with an initial GAC date and visible creation date.
      if (requestData?.map
        && Array.isArray(requestData)
        && Object.isExtensible(requestData)) {
        requestData.push(
          {
            fieldName: gacChangeHistoryReportFieldMapping.GAC_CHANGE_HISTORY_INITIAL_GAC_DATE,
            function: 'IS_DEFINED'
          }
        );
        if (!requestData.map((field) => field.fieldName).includes('poHeader.poCreatedOn')) {
          requestData.push(
            {
              fieldName: 'poHeader.poCreatedOn',
              operator: '>',
              fieldValue: appConfig.gacChangeHiddenCreateDate
            }
          );
        }
      }
      let requestBody = (state.gacChangeHistoryReport.gacChangeHistoryReportRequestBody)
        ? {
          ...state.gacChangeHistoryReport.gacChangeHistoryReportRequestBody,
          search: requestData,
          offset: '0',
          ...modifiedrequestBody
        } : {
          fields: fieldsForRequest,
          search: requestData,
          count: appConfig.SearchThresholdLimit,
          offset: '0',
          ...modifiedrequestBody
        };
      requestBody = setValidFields(
        gacChangeHistoryReportFieldProperties, requestBody, [], inactiveFeatureFields
      );

      const payload = {
        ...requestBody,
        fields: [
          ...requestBody.fields,
          // Merge required backend fields without duplicating user-selected fields.
          ...appConfig.includeFieldsGacChangeHistory.filter(
            (item) => !requestBody.fields.includes(item)
          )
        ]
      };
      request({
        api: 'gacChangeHistorySearch',
        method: 'post',
        data: showForTypePayload ? { ...payload, type: 'gacChangeHistory' } : payload,
        cancellable: true
      }, dispatch).then((response) => {
        dispatch(actions.updateGACChangeHistoryReportRequestBody(requestBody));
        dispatch(toggleSpinner(false));
        callback(response.data);
        dispatch(actions.updateGACChangeHistoryReportRowsPerPage(rowsPerPageOption));
        dispatch(actions.updateGACChangeHistoryReportResults(response));
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        callback(null, error);
      });
    }
  );
}

/**
 * Async thunk that fetches subtable GAC history data for a selected report row.
 */
function fetchGACChangeHistorySubTable() {
  return createAsyncThunk(
    `${name}/fetchGACChangeHistorySubTable`,
    async ({
      rowId, callback
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const fields = [
        gacChangeHistoryReportFieldMapping.GAC_CHANGE_HISTORY_PO_NUMBER,
        gacChangeHistoryReportFieldMapping.GAC_CHANGE_HISTORY_ITEM_NUMBER,
        'dpomGacHistoryData'
      ];
      const state = getState();
      const requestData = state.gacChangeHistoryReport.gacChangeHistoryReportRequestBody.search;
      // Subtable requests reuse the parent search criteria and request only row-detail fields.
      const requestBody = {
        search: requestData,
        offset: '0',
        fields: [...fields],
        count: appConfig.SearchThresholdLimit
      };
      request({
        api: 'gacChangeHistorySubtable',
        method: 'post',
        data: showForTypePayload ? { ...requestBody, type: 'gacChangeHistory' } : requestBody,
        cancellable: true
      }, dispatch).then((response) => {
        dispatch(actions.updateGACChangeHistorySubtableRequestBody(requestBody));
        // Keep only the subtable row that matches the expanded parent row.
        const filteredData = response.data.objects.filter((x) => x.id === rowId);
        callback(filteredData);
        dispatch(actions.updateGACChangeHistorySubtableResults({ data: filteredData }));
        dispatch(toggleSpinner(false));
      }).catch((error) => {
        callback(null, error);
        dispatch(toggleSpinner(false));
      });
    }
  );
}
/**
 * Async thunk that prepares export fields and calls gacChangeHistoryReportExportLargeFile.
 */
function exportGACChangeHistoryReport() {
  return createAsyncThunk(
    `${name}/exportGACChangeHistoryReport`,
    async ({
      callback,
      filetype,
      inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      const state = getState();
      dispatch(toggleSpinner(true));
      const searchRequestBody = {
        ...state.gacChangeHistoryReport.gacChangeHistoryReportRequestBody
      };
      // Work on a cloned request so export-only field changes do not mutate search state.
      const tempRequestData = {
        ...searchRequestBody,
        fields: Array.isArray(searchRequestBody.fields)
          ? [...searchRequestBody.fields]
          : []
      };
      tempRequestData.fields.push(...appConfig.includeFieldsGACHistorySubTable);

      // Remove hidden backing fields from export output unless they are explicitly duplicated.
      const removeAdditionalFields = new Set(
        gacChangeHistoryReportHeader.filter((column) => column.additionalFields)
          .map((headRow) => headRow.additionalFields[0])
      );
      const filedsIncludingDuplicate = tempRequestData.fields.filter(
        (item) => !removeAdditionalFields.has(item)
      );
      const duplicateKeysAre = filedsIncludingDuplicate.filter(
        (keys, index) => filedsIncludingDuplicate.indexOf(keys) !== index
      );
      const fieldToExcludes = [
        ...appConfig.excludedPOFieldsInExportedFile,
        ...gacChangeHistoryReportHeader.filter((column) => column.includeInRequest)
          .map((headRow) => headRow.primary)
      ];
      let distinctAppConfigKeys = [];
      if (duplicateKeysAre.length === 0) {
        distinctAppConfigKeys = [...fieldToExcludes];
      } else {
        distinctAppConfigKeys = fieldToExcludes.filter((duplicated) => (
          !duplicateKeysAre.includes(duplicated)
        ));
      }
      const gacChangeHistoryReportRequestBody = {
        search: [...(searchRequestBody.search) || []],
        fields: filedsIncludingDuplicate.filter((field: string) => (
          !distinctAppConfigKeys.includes(field)
        )),
        searchType: searchRequestBody.searchType,
        fileFormat: filetype,
        type: 'gacChangeHistory'
      };
      console.log('inactiveFeatureFields', inactiveFeatureFields);
      // TODO: handle inactive feature fields
      // gacChangeHistoryReportRequestBody = setValidFields(
      //   gacChangeHistoryReportFieldProperties,
      //   gacChangeHistoryReportRequestBody,
      //   [],
      //   inactiveFeatureFields
      // );
      request({
        api: 'gacChangeHistoryReportExportLargeFile',
        method: 'post',
        data: showForTypePayload ? { ...gacChangeHistoryReportRequestBody, type: 'gacChangeHistory' } : gacChangeHistoryReportRequestBody
      }, dispatch).then((response) => {
        callback(response);
        dispatch(toggleSpinner(false));
      }).catch((error) => {
        callback(null, error);
        dispatch(toggleSpinner(false));
      });
    }
  );
}
/**
 * Async thunk that executes a saved GAC change history search and restores its UI state.
 */
function fetchSavedGACChangeHistoryReportResults() {
  return createAsyncThunk(
    `${name}/fetchSavedGACChangeHistoryReportResults`,
    async ({
      gacChangeHistoryReportRequestData, callback, inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      const state = getState();
      let requestBody = { ...gacChangeHistoryReportRequestData.searchCriteria, offset: '0' };
      requestBody = setValidFields(
        gacChangeHistoryReportFieldProperties, requestBody, [], inactiveFeatureFields
      );

      // Saved searches must always constrain results to records with an initial GAC date.
      if (requestBody.search?.map
        && !requestBody.search.some((criteria) => (
          criteria.fieldName === gacChangeHistoryReportFieldMapping
            .GAC_CHANGE_HISTORY_INITIAL_GAC_DATE
        ))
      ) {
        requestBody.search.push({
          fieldName: gacChangeHistoryReportFieldMapping.GAC_CHANGE_HISTORY_INITIAL_GAC_DATE,
          function: 'IS_DEFINED'
        });
      }
      const {
        rowPerPageOption: rowsPerPageOption
      } = state?.searchpaneldata || {};
      dispatch(toggleSpinner(true));
      dispatch(updateDialogStates({ executeBookmarkSpinner: true }));
      const payload = {
        ...requestBody,
        fields: [
          ...requestBody.fields,
          ...appConfig.includeFieldsGacChangeHistory.filter(
            (item) => !requestBody.fields.includes(item)
          )
        ]
      };
      request({
        api: 'gacChangeHistorySearch',
        method: 'post',
        data: showForTypePayload ? {
          savedSearchID: gacChangeHistoryReportRequestData.id,
          ...payload,
          type: 'gacChangeHistory'
        } : {
          savedSearchID: gacChangeHistoryReportRequestData.id,
          ...payload
        },
        cancellable: true
      }, dispatch).then((response) => {
        // Rehydrate saved search criteria into the search panel input model.
        const fields = mapRequestToInputFields([
          ...gacChangeHistoryReportRequestData.searchCriteria.search
        ]);
        const gacChangeHistorySearchFields = new Set(
          gacChangeHistoryReportRequestData.searchCriteria.search.map((field) => field.fieldName)
        );
        dispatch(actions.updateGACChangeHistoryReportInputValues(
          fields
        ));
        dispatch(actions.updateGACChangeHistoryReportRequestBody(requestBody));
        dispatch(actions.updateGACChangeHistoryReportFields(gacChangeHistoryReportFieldList
          .map((field) => field.key)
          .filter((field) => gacChangeHistorySearchFields.has(field))));
        dispatch(actions.updateGACChangeHistoryReportResults(response));
        dispatch(actions.updateGACChangeHistoryReportSearchId());
        dispatch(actions.updateGACChangeHistoryReportPage(0));
        dispatch(actions.updateGACChangeHistoryReportRowsPerPage(rowsPerPageOption));
        dispatch(actions.updateGACChangeHistoryReportBookmarkId(
          gacChangeHistoryReportRequestData.id
        ));
        dispatch(
          actions.updateGACChangeHistoryReportBookmarkName(
            gacChangeHistoryReportRequestData.name
          )
        );
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
        if (callback) {
          callback(response);
        }
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
        console.log(error);
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}
/**
 * Async thunk that refetches report results from offset zero using the current request body.
 */
function fetchGACChangeHistoryReportsResultsOffset() {
  return createAsyncThunk(
    `${name}/fetchGACChangeHistoryReportsResultsOffset`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.gacChangeHistoryReport.gacChangeHistoryReportRequestBody
                  && state.gacChangeHistoryReport.gacChangeHistoryReportResultData) {
        // Reset to the first result window while preserving the current request criteria.
        let gacChangeHistoryReportUpdatedRequest = state.gacChangeHistoryReport
          .gacChangeHistoryReportRequestBody;
        gacChangeHistoryReportUpdatedRequest = {
          ...gacChangeHistoryReportUpdatedRequest,
          offset: '0'
        };
        dispatch(toggleSpinner(true));
        const payload = {
          ...gacChangeHistoryReportUpdatedRequest,
          fields: [
            ...gacChangeHistoryReportUpdatedRequest.fields,
            ...appConfig.includeFieldsGacChangeHistory.filter(
              (item) => !gacChangeHistoryReportUpdatedRequest.fields.includes(item)
            )
          ]
        };
        request({
          api: 'gacChangeHistorySearch',
          method: 'post',
          data: showForTypePayload ? { ...payload, type: 'gacChangeHistory' } : payload
        }, dispatch).then((response) => {
          dispatch(actions.gacChangeHistoryReportOffset(response));
          dispatch(toggleSpinner(false));
          dispatch(actions.updateGACChangeHistoryReportSearchId());
        }).catch((error) => {
          console.log('NEXT Error ', error);
          dispatch(toggleSpinner(false));
        });
      }
    }
  );
}
/**
 * Async thunk wrapper that dispatches the supplied offset-fetch action.
 */
function gacChangeHistoryReportDataOffset() {
  return createAsyncThunk(
    `${name}/gacChangeHistoryReportDataOffset`,
    async (
      {
        gacFetchGACChangeHistoryReportsResultsOffset
      }, { dispatch }
    ) => {
      dispatch(gacFetchGACChangeHistoryReportsResultsOffset());
    }
  );
}
/**
 * Async thunk that fetches the next result set for jump-to-page pagination.
 */
function fetchGACChangeHistoryReportJumpToPageNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchGACChangeHistoryReportJumpToPageNextResultSet`,
    async (props, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallAfterAppend(false));
      dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
      if (state.gacChangeHistoryReport.gacChangeHistoryReportRequestBody
  && state.gacChangeHistoryReport.gacChangeHistoryReportResultData
  && state.gacChangeHistoryReport.gacChangeHistoryReportResultData.pages.next) {
        const currentResultSet = state.gacChangeHistoryReport
          .gacChangeHistoryReportResultData;
        let updatedRequest = state.gacChangeHistoryReport
          .gacChangeHistoryReportRequestBody;
        const searchResultNextOffset = state.gacChangeHistoryReport
          .gacChangeHistoryReportResultData.pages.next;
        const resNext = searchResultNextOffset.indexOf(',');
        // The API cursor is encoded like offset=<value>, so extract the numeric offset.
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultNextOffset.slice(7, resNext)
        };
        const payload = {
          ...updatedRequest,
          fields: [
            ...updatedRequest.fields,
            ...appConfig.includeFieldsGacChangeHistory.filter(
              (item) => !updatedRequest.fields.includes(item)
            )
          ]
        };
        request({
          api: 'gacChangeHistorySearch',
          method: 'post',
          data: showForTypePayload ? { ...payload, type: 'gacChangeHistory' } : payload
        }, dispatch).then((response) => {
          dispatch(actions.updateGACChangeHistoryRequestBodyOnSameBookmark(updatedRequest));
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
          dispatch(toggleSpinner(false));
          dispatch(actions.updateGACChangeHistoryReportNextResult({
            response, currentResultSet
          }));
        }).catch((error) => {
          dispatch(toggleSpinner(false));
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
          console.log('NEXT Error ', error);
        });
      }
    }
  );
}

/**
 * Async thunk that fetches the next page-window result set from gacChangeHistorySearch.
 */
function fetchGACChangeHistoryReportNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchGACChangeHistoryReportNextResultSet`,
    async (props, { getState, dispatch }) => {
      const state = getState();
      dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
      if (state.gacChangeHistoryReport.gacChangeHistoryReportRequestBody
  && state.gacChangeHistoryReport.gacChangeHistoryReportResultData
  && state.gacChangeHistoryReport.gacChangeHistoryReportResultData.pages.next) {
        const currentResultSet = state.gacChangeHistoryReport
          .gacChangeHistoryReportResultData;
        const currentJumpToPage = state.gacChangeHistoryReport.jumpToPage || 0;
        // Jump pagination aligns requested pages to the 1,000-row result-window boundary.
        const pageGroupSize = 1000 / state.gacChangeHistoryReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.gacChangeHistoryReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.gacChangeHistoryReport
          .gacChangeHistoryReportRequestBody;
        const searchResultNextOffset = state.gacChangeHistoryReport
          .gacChangeHistoryReportResultData.pages.next;
        const resNext = searchResultNextOffset.indexOf(',');
        // The API cursor is encoded like offset=<value>, so extract the numeric offset.
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            ? (pageGroup * 1000).toString() : searchResultNextOffset.slice(7, resNext)
        };
        const payload = {
          ...updatedRequest,
          fields: [
            ...updatedRequest.fields,
            ...appConfig.includeFieldsGacChangeHistory.filter(
              (item) => !updatedRequest.fields.includes(item)
            )
          ]
        };
        request({
          api: 'gacChangeHistorySearch',
          method: 'post',
          data: showForTypePayload ? { ...payload, type: 'gacChangeHistory' } : payload
        }, dispatch).then((response) => {
          dispatch(actions.updateGACChangeHistoryRequestBodyOnSameBookmark(updatedRequest));
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
          dispatch(toggleSpinner(false));
          dispatch(actions.updateGACChangeHistoryReportNextResult({
            response, currentResultSet
          }));
          if (triggerJumpToPage) {
            dispatch(actions.ChangeReportAppendJumpToPageNextResultSet(
              state.gacChangeHistoryReport.rowsPerPage
            ));
            dispatch(actions.updatePreviousResultSettoNull());
          }
        }).catch((error) => {
          dispatch(toggleSpinner(false));
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
          console.log('NEXT Error ', error);
        });
      }
    }
  );
}

/**
 * Async thunk wrapper that dispatches the supplied next-result fetch action.
 */
function ChangeReportsDataNextSet() {
  return createAsyncThunk(
    `${name}/ChangeReportsDataNextSet`,
    async (
      {
        gacFetchGACChangeHistoryReportNextResultSet
      }, { dispatch }
    ) => {
      dispatch(gacFetchGACChangeHistoryReportNextResultSet());
    }
  );
}

/**
 * Async thunk that fetches the previous result set for jump-to-page pagination.
 */
function fetchReportJumpToPagePrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportJumpToPagePrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallBeforePrepend(false));
      if (state.gacChangeHistoryReport
              && state.gacChangeHistoryReport.gacChangeHistoryReportResultData
              && state.gacChangeHistoryReport.gacChangeHistoryReportResultData.pages.prev) {
        const currentResultSet = state.gacChangeHistoryReport
          .gacChangeHistoryReportResultData;
        let updatedRequest = state.gacChangeHistoryReport
          .gacChangeHistoryReportRequestBody;
        const searchResultPrevOffset = state.gacChangeHistoryReport
          .gacChangeHistoryReportResultData.pages.prev;
        const res = searchResultPrevOffset.indexOf(',');
        // The API cursor is encoded like offset=<value>, so extract the numeric offset.
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultPrevOffset.slice(7, res)
        };
        const payload = {
          ...updatedRequest,
          fields: [
            ...updatedRequest.fields,
            ...appConfig.includeFieldsGacChangeHistory.filter(
              (item) => !updatedRequest.fields.includes(item)
            )
          ]
        };
        dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
        request({
          api: 'gacChangeHistorySearch',
          method: 'post',
          data: showForTypePayload ? { ...payload, type: 'gacChangeHistory' } : payload
        }, dispatch).then((response) => {
          dispatch(actions.updateGACChangeHistoryRequestBodyOnSameBookmark(updatedRequest));
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
          dispatch(toggleSpinner(false));
          dispatch(actions.updateGACChangeHistoryReportPrevResult({
            response, currentResultSet
          }));
        }).catch((error) => {
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
          dispatch(toggleSpinner(false));
          console.log('PREV Error ', error);
        });
      }
    }
  );
}

/**
 * Async thunk that fetches the previous page-window result set from gacChangeHistorySearch.
 */
function fetchReportPrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportPrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.gacChangeHistoryReport
              && state.gacChangeHistoryReport.gacChangeHistoryReportResultData
              && state.gacChangeHistoryReport.gacChangeHistoryReportResultData.pages.prev) {
        const currentResultSet = state.gacChangeHistoryReport
          .gacChangeHistoryReportResultData;
        const currentJumpToPage = state.gacChangeHistoryReport.jumpToPage || 0;
        // Jump pagination aligns requested pages to the 1,000-row result-window boundary.
        const pageGroupSize = 1000 / state.gacChangeHistoryReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.gacChangeHistoryReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.gacChangeHistoryReport
          .gacChangeHistoryReportRequestBody;
        const searchResultPrevOffset = state.gacChangeHistoryReport
          .gacChangeHistoryReportResultData.pages.prev;
        const res = searchResultPrevOffset.indexOf(',');
        // The API cursor is encoded like offset=<value>, so extract the numeric offset.
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            ? (pageGroup * 1000).toString() : searchResultPrevOffset.slice(7, res)
        };
        const payload = {
          ...updatedRequest,
          fields: [
            ...updatedRequest.fields,
            ...appConfig.includeFieldsGacChangeHistory.filter(
              (item) => !updatedRequest.fields.includes(item)
            )
          ]
        };
        dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
        request({
          api: 'gacChangeHistorySearch',
          method: 'post',
          data: showForTypePayload ? { ...payload, type: 'gacChangeHistory' } : payload
        }, dispatch).then((response) => {
          dispatch(actions.updateGACChangeHistoryRequestBodyOnSameBookmark(updatedRequest));
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
          dispatch(toggleSpinner(false));
          dispatch(actions.updateGACChangeHistoryReportPrevResult({
            response, currentResultSet
          }));
          if (triggerJumpToPage) {
            dispatch(actions.ChangeReportAppendJumpToPagePrevResultSet(
              state.gacChangeHistoryReport.rowsPerPage
            ));
            dispatch(actions.updateNextResultSettoNull());
          }
        }).catch((error) => {
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
          dispatch(toggleSpinner(false));
          console.log('PREV Error ', error);
        });
      }
    }
  );
}

/**
 * Async thunk wrapper that dispatches the supplied previous-result fetch action.
 */
function ChangeReportsDataPrevSet() {
  return createAsyncThunk(
    `${name}/ChangeReportsDataPrevSet`,
    async (
      {
        gacfetchReportPrevResultSet
      }, { dispatch }
    ) => {
      dispatch(gacfetchReportPrevResultSet());
    }
  );
}
/**
 * Async thunk that fetches GAC reason codes from getGACReasonCode.
 */
function getGacReasonCode() {
  return createAsyncThunk(
    `${name}/getGacReasonCode`,
    async ({
      callback
    }, { dispatch }) => {
      const payload = {};
      dispatch(toggleSpinner(true));
      request({
        api: 'getGACReasonCode',
        method: 'get'
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        payload.gacReasonCode = response.data;
        if (callback) {
          callback(response);
        }
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}
/**
 * Builds thunk action creators exported with the GAC change history report slice actions.
 */
function createExtraActions() {
  return {
    fetchGACChangeHistoryReports: fetchGACChangeHistoryReports(),
    fetchGACChangeHistorySubTable: fetchGACChangeHistorySubTable(),
    exportGACChangeHistoryReport: exportGACChangeHistoryReport(),
    fetchSavedGACChangeHistoryReportResults: fetchSavedGACChangeHistoryReportResults(),
    fetchGACChangeHistoryReportsResultsOffset: fetchGACChangeHistoryReportsResultsOffset(),
    gacChangeHistoryReportDataOffset: gacChangeHistoryReportDataOffset(),
    fetchGACChangeHistoryReportNextResultSet: fetchGACChangeHistoryReportNextResultSet(),
    ChangeReportsDataNextSet: ChangeReportsDataNextSet(),
    fetchReportPrevResultSet: fetchReportPrevResultSet(),
    ChangeReportsDataPrevSet: ChangeReportsDataPrevSet(),
    getGacReasonCode: getGacReasonCode(),
    fetchGACChangeHistoryReportJumpToPageNextResultSet:
      fetchGACChangeHistoryReportJumpToPageNextResultSet(),
    fetchReportJumpToPagePrevResultSet: fetchReportJumpToPagePrevResultSet()
  };
}

export const extraActions = createExtraActions();

export const gacChangeReportActions = { ...actions, ...extraActions };
export const gacChangeReportReducer = slice.reducer;
